import React, { useState } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <>
      <style jsx>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
      `}</style>
      <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" className="flex items-center">
              <img 
                src="/pandanewlogo.png" 
                alt="Total Repair UK Logo" 
                className="w-32 h-32 rounded-lg"
              />
              <span className="hidden sm:block -ml-6">
              <span className="text-2xl font-black bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto] hover:scale-105 transition-transform duration-300 inline-block">
Total Repair              </span>
              <span className="text-2xl font-light bg-gradient-to-r from-slate-700 via-slate-500 to-slate-700 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto] hover:scale-105 transition-transform duration-300 inline-block ml-2">
                UK
              </span>
            </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-slate-600 hover:text-slate-900 transition-colors font-medium"
              >
                {item.name}
              </Link>
            ))}
            <a
              href="tel:+15551234567"
              className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">(555) 123-4567</span>
            </a>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation - Bento Style */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="md:hidden absolute top-16 left-0 right-0 bg-white shadow-2xl border border-slate-200 rounded-b-3xl overflow-hidden z-50"
            >
              {/* Bento Grid Container */}
              <div className="p-4 grid grid-cols-2 gap-3">
                
                {/* Logo & Name Card - Full Width */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="col-span-2 bg-gradient-to-r from-slate-900 to-slate-700 rounded-2xl p-4 flex items-center justify-center"
                >
                  <div className="text-center">
                    <div className="text-white font-bold text-xl">Perfect Panda</div>
                    <div className="text-slate-300 text-sm">Repairs</div>
                  </div>
                </motion.div>

                {/* Navigation Cards */}
                {navigation.map((item, idx) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + idx * 0.1 }}
                    whileHover={{ scale: 1.05, rotateY: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-lg hover:border-slate-300 transition-all group"
                  >
                    <Link
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="block"
                    >
                      <div className="text-slate-900 font-semibold text-base mb-1 group-hover:text-slate-700 transition-colors">
                        {item.name}
                      </div>
                      <div className="text-slate-500 text-xs">
                        {item.name === 'Home' && 'Main Page'}
                        {item.name === 'About' && 'Our Story'}
                        {item.name === 'Services' && 'What We Do'}
                        {item.name === 'Pricing' && 'Emergency Rates'}
                        {item.name === 'Contact' && 'Get In Touch'}
                      </div>
                    </Link>
                  </motion.div>
                ))}

                {/* Phone Card - Full Width */}
                <motion.a
                  href="tel:+15551234567"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="col-span-2 bg-slate-900 rounded-2xl p-4 flex items-center justify-center gap-3 hover:bg-slate-800 transition-all"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-white">
                    <div className="font-bold">Call Now</div>
                    <div className="text-sm opacity-90">(555) 123-4567</div>
                  </div>
                </motion.a>

                {/* Social/Quick Actions */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="col-span-2 flex justify-center"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-slate-900 text-white rounded-2xl p-3 font-medium hover:bg-slate-800 transition-colors"
                  >
                    Get Quote
                  </motion.button>
                </motion.div>

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
    </>
  );
}
